import React, { useEffect, useState, useRef } from 'react'
import {format} from 'date-fns';
import Table from '../../components/Table/Table'
import tableCheckbox from '../../components/Inputs/tableCheckbox'
import downloadIcon from '../../assets/icons/table/download.svg'
import trashIcon from '../../assets/icons/table/trash.svg'
import ConfirmModal from '../../components/Modals/ConfirmModal'
import API from '../../utils/API'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import constants from '../../utils/constants'
import { FormattedMessage } from 'react-intl';
import useInfiniteScroll from '../../components/common/useInfiniteScroll';

function CMSManagement(props) {
  const navigate = useNavigate();
  const { user } = props;
  const dateOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  const datetimeOptions = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
  const permissionIds = user.permissions.map((permission)=>{
    return permission.id
  })
  
  if(!permissionIds.includes(constants.permissionIds.support)){
    navigate('/forbidden');
  };

  const [selectedCMSes, setSelectedCMSes] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [actionType, setActionType] = useState('');

  // Give state to all components of the dataMap(explained later) you want to render
  const [data, setData] = React.useState([]);
  const [dataCount, setDataCount] = React.useState(0);
  const [totalDataCount, setTotalDataCount] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState({});
  const [sortByCol, setSortByCol] = React.useState('name');
  const [sortByAsc, setSortByAsc] = React.useState(true);
  const [startIndexForTable, setStartIndexForTable] = React.useState(0);
  const [searchValue, setSearchValue] = useState('');

  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const searchRef = useRef();

  searchRef.current = searchValue;
 
  const dataMap = React.useMemo(
    () => {
      const map = {data: {}, ids: [], selectedIds: {}, filteredIds: []};
      loadTable(map);
      return map;
    }
  , [])

  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    startIndexForTable,
    setStartIndexForTable,
    pageSize: 100, 
    sortByCol,
    setSortByCol,
    sortByAsc, 
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  /**
   * Load table with users
   */
  function loadTable(map, resetSelection=true) {
    //Reset map
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if(resetSelection){
      map.selectedIds = {}
    }

    API.getCmses()
      .then((res) => {
        console.log('cms res:', res.data);
        res.data.forEach((cms) => {
          let statusType = 'danger';
          let statusValue = 'Disconnected';
          const lastConnected = new Date(cms.last_ping);
          // Check if last ping is within 70 seconds
          if(lastConnected > Date.now() - (1000*70)) {
            statusType = 'success';
            statusValue = 'Connected';
          }

          // Determine the display value for 'lastPing'
          const lastSeenDisplay = !cms.last_ping
            ? 'Unknown'
            : new Date(cms.last_ping).toLocaleString(undefined, datetimeOptions);

          const createdAtDisplay = new Date(cms.created_at).toLocaleString(undefined, dateOptions);

          map.data[cms.id] = {
            id: cms.id,
            accountName: cms.account_name,
            accountId: cms.account_id,
            connectionLogs: cms.connection_logs,
            createdAt: format(new Date(cms.created_at), 'yyyy-MM-dd HH:mm:ss'),
            createdAtDisplay,
            lastPing: !cms.last_ping ? null : format(new Date(cms.last_ping), 'yyyy-MM-dd HH:mm:ss'),
            lastSeenDisplay,
            name: cms.name,
            sites: cms.sites,
            version: cms.version,
            siteNames: cms.sites.map((site)=>{return site.name}).join(', '),
            connection_logs: cms.connection_logs,
            statusType,
            statusValue,
            checked: false,
          }
        })
        map.filteredIds = Object.keys(map.data);
        map.ids = Object.keys(map.data);
        console.log('CMSes:', map.data);
        
        // Call React setState to render a section of our data
        if(searchRef.current !== ""){
          tableRef.current.search(map)
        }else{
          sortMap(map);
          setData(generateDataSlice(map));
          setDataCount(map.sortedIds.length);
        }
        setSelectedIds(map.selectedIds);
        setTotalDataCount(map.ids.length);
        setDataLoaded(true);
      })
      .catch(err => {
        console.error(err.message);
        setDataLoaded(true);
      });
  }

  /**
   * NOTE 9/19/24: NO LONGER USED
   * handle downloading connection logs of CMS
   * @param {Event} e 
   * @param {Object} cms CMS data object
   */
  function handleDownloadLogs(e, cms) {
    e.preventDefault();

    const logs = cms.connectionLogs;

    // Convert logs to CSV format
    const csvContent = "data:text/csv;charset=utf-8," +
      "Timestamp,Log\n" + // Add the headers
      logs.map(log => `${log.timestamp},${log.log}`).join("\n");

    // Create a download link and trigger the download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `cms_logs_${cms.id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`Download logs for CMS ID: ${cms.id}`);
  }

  /**
   * Handle click to delete a specific user.
   * @param {string} id 
   */
  function handleSingleDelete(e, id) {
    e.preventDefault();
    let clickedCMS = dataMap.data[id];
    console.log('clicked CMS:', clickedCMS);
    setSelectedCMSes(clickedCMS);
    setActionType('single');
  }

  function handleDeleteCMSes(e) {
    e.preventDefault();
    // Get selected IDs from dataMap
    const idsToDeleteFromMap = Object.keys(dataMap.selectedIds)
      .filter(id => dataMap.selectedIds[id] === 1); // Filter selected IDs

    // Include selectedUser ID if it exists
    const idsToDelete = selectedCMSes ? [...idsToDeleteFromMap, selectedCMSes.id] : idsToDeleteFromMap;
    console.log('idsToDelete:', idsToDelete);

    if (idsToDelete.length === 0) {
      setError(<FormattedMessage id="NO_CMSES_TO_DELETE" />);
      return; 
    }

    API.deleteCmses(idsToDelete)
      .then((res)=>{
        loadTable(dataMap);
        setSuccess(<FormattedMessage id="DELETE_CMSES_SUCCESS" />);
      }).catch((err)=>{
        console.error(err.message);
        setError(<FormattedMessage id="DELETE_CMSES_FAILED" />);
      })
    closeModal('confirmModal');
  }

  /**
   * Closes modal given the modal id
   * @param {*} modalId - id of modal to close
   */
  function closeModal(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
      const closeButton = modal.querySelector('.btn-close');
      if (closeButton) {
        closeButton.click();
      }
    }
    // loadTable();
  }

  const tableColumns = React.useMemo(
    () => [
      {
        Header: 'ACCOUNT_NAME',
        id: 'accountName',
        accessor: 'accountName',
        isVisible: true,
      },
      {
        Header: 'ACCOUNT_ID',
        id: 'accountId',
        accessor: 'accountId',
        isVisible: true,
      },
      {
        Header: 'STATUS',
        id: 'status',
        accessor: 'statusValue',
        Cell: ({ row }) => (
          <span className={`text-${row.original.statusType}`}>
            {row.original.statusValue}
          </span>
        ),
        isVisible: true,
      },
      {
        Header: 'LAST_SEEN',
        id: 'lastPing',
        accessor: 'lastSeenDisplay', 
        isVisible: true,
        Cell: ({ value, row }) => (
          <span className={`text-${row.original.statusType}`}>
            {value}
          </span>
        ),
      },
      {
        Header: 'NAME',  
        id: 'name',  
        accessor: 'name',  
        isVisible: true,  
      },
      {
        Header: 'SITES',
        id: 'siteNames',
        accessor: 'siteNames',
        isVisible: true,
      },
      {
        Header: 'ID',
        id: 'id',
        accessor: 'id',
        isVisible: true,
      },
      {
        Header: 'BORN_ON_DATE',  
        id: 'createdAt',  
        accessor: 'createdAtDisplay',
        isVisible: true,  
      },
      {
        Header: 'VERSION',  
        id: 'version',  
        accessor: 'version',  
        isVisible: true,  
      },
      {
        Header: '',
        id: 'actions',
        isVisible: true,
        Cell: ({ row }) => {
          return (
            <div className="actions">
              <button
                className="btn"
                data-bs-toggle="modal"
                data-bs-target={`#confirmModal`}
                onClick={(e) => {
                  e.stopPropagation(); 
                  if (handleSingleDelete) handleSingleDelete(e, row.original.id);
                }}
              >
                <img src={trashIcon} className="actionIcon deleteAction" alt={<FormattedMessage id="DELETE" />} />
              </button>
            </div>
          );
        }
      }
    ])

  // Props to pass to our table 
    const tableProps = {
    pageType: constants.pageType.CMSManagement,
    modalId: null,
    columns: tableColumns,
    // Data Handling props
    data, setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    // Sorting
    setSortBy,
    sortByCol,
    sortByAsc,
    // Error handling
    success, setSuccess,
    error, setError,
    // Common props
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue, 
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    // Action column props
    handleSingleDelete,
  } 

  const confirmModalProps = {
    actionType,
    selectedItem: selectedCMSes,
    checkedItems: Object.keys(selectedIds),
    handleDelete: handleDeleteCMSes,
    closeModal,
    dataType: 'CMS',
  }

  return (
    <>
      <ConfirmModal {...confirmModalProps} />
      <Table {...tableProps} />
    </>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(CMSManagement)